import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import client, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Home, MapPin, KeyRound, Car, Radar, Loader2, Briefcase, Search, Power, Settings2, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { BatchBar } from "@/components/BatchBar";
import { toast } from "sonner";
import { useCustomFields, CustomFieldManager } from "@/components/CustomFields";
import { usePoll } from "@/hooks/usePoll";
import { confirm } from "@/components/ConfirmDialog";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { SearchSelect } from "@/components/SearchSelect";
import { PropertyImport } from "@/components/PropertyImport";
import { Upload } from "lucide-react";

const EMPTY = { clientId: "", address: "", geo: { lat: "", lng: "" }, geofenceRadius: 25, geofenceEnabled: false, accessInfo: "", parkingInfo: "", what3words: "", instructions: "", notes: "", assignedWorkerIds: [] };

export default function Properties() {
  const { t } = useI18n();
  const { can } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [cfMgr, setCfMgr] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const { reload: reloadDefs } = useCustomFields("property");
  const [sel, setSel] = useState({});
  const selIds = Object.keys(sel).filter((k) => sel[k]);

  const batch = async (action) => {
    if (!selIds.length) return;
    if (!(await confirm({ title: `${action} properties`, description: `${action} ${selIds.length} propert${selIds.length === 1 ? "y" : "ies"}?`, confirmText: action, destructive: action === "delete" || action === "deactivate" }))) return;
    try {
      const { data } = await client.post("/properties/batch", { ids: selIds, action });
      const reasons = [...new Set((data.skipped || []).map((s) => s.reason).filter(Boolean))].join("; ");
      if (data.done.length) toast.success(`${data.done.length} done${data.skipped.length ? ` · ${data.skipped.length} skipped (${reasons})` : ""}.`);
      else toast.warning(`Nothing deleted — ${data.skipped.length} skipped${reasons ? `: ${reasons}` : ""}.`);
      setSel({}); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const load = () => client.get(`/properties?includeInactive=${includeInactive}`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); client.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, [includeInactive]); // eslint-disable-line react-hooks/exhaustive-deps
  usePoll(load, 15000);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = !s ? rows : rows.filter((p) => [p.address, p.clientName, p.what3words, p.accessInfo, p.notes].filter(Boolean).some((v) => v.toLowerCase().includes(s)));
    return [...list].sort((a, b) => (a.address || "").localeCompare(b.address || ""));
  }, [rows, q]);
  const allSelected = filtered.length > 0 && filtered.every((p) => sel[p.id]);
  const toggleAll = (v) => { const next = { ...sel }; filtered.forEach((p) => { next[p.id] = !!v; }); setSel(next); };

  const create = async () => {
    setBusy(true);
    try {
      await client.post("/properties", { ...form, geofenceRadius: Number(form.geofenceRadius) || 25, geo: { lat: parseFloat(form.geo.lat) || null, lng: parseFloat(form.geo.lng) || null } });
      toast.success(t("toast.propertySaved", "Property saved")); setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const createClientInline = async (name) => {
    const { data } = await client.post("/clients", { name, contact: "" });
    const list = (await client.get("/clients")).data; setClients(list);
    const id = data?.id || (list.find((c) => c.name === name) || {}).id;
    setForm((f) => ({ ...f, clientId: id }));
    toast.success(t("toast.clientSaved", "Client saved"));
  };

  const F = ({ label, k, icon: Icon }) => (
    <div><Label className="flex items-center gap-1.5">{Icon && <Icon className="h-3.5 w-3.5 text-slate-400" />}{label}</Label>
      <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5" /></div>
  );

  return (
    <div className="space-y-5" data-testid="properties-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-brand-ink">{t("nav.properties", "Properties")}</h2>
          <p className="text-sm text-slate-500">{t("prop.subtitle", "Properties, access details, geofence and related jobs.")}</p>
        </div>
        {can("property.manage") && (
          <div className="flex gap-2">
          <Button variant="outline" data-testid="property-import-button" onClick={() => setImportOpen(true)}><Upload className="h-4 w-4 mr-2" />Import in bulk</Button>
          <PropertyImport open={importOpen} onOpenChange={setImportOpen} clients={clients} onDone={load} />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="property-create-button" className="bg-brand hover:bg-brand-dark"><Plus className="h-4 w-4 mr-2" />{t("prop.new", "New property")}</Button></DialogTrigger>
            <DialogContent data-testid="property-create-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{t("prop.new", "New property")}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>{t("prop.client", "Client")}</Label>
                  <SearchSelect
                    testid="property-client-select"
                    value={form.clientId}
                    onChange={(v) => setForm({ ...form, clientId: v })}
                    options={clients.map((c) => ({ value: c.id, label: c.name }))}
                    placeholder={t("prop.client", "Client")}
                    searchPlaceholder={t("job.searchClient", "Search or add a client…")}
                    onCreate={createClientInline}
                    createLabel={t("job.addClient", "Create client")}
                    className="mt-1.5"
                  />
                </div>
                <div><Label>{t("prop.address", "Address")}</Label><AddressAutocomplete testid="property-address-input" value={form.address} onChange={(v) => setForm({ ...form, address: v })} onSelect={(r) => setForm((f) => ({ ...f, address: r.address, geo: { lat: r.lat ?? "", lng: r.lng ?? "" } }))} className="mt-1.5" /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Lat</Label><Input value={form.geo.lat} onChange={(e) => setForm({ ...form, geo: { ...form.geo, lat: e.target.value } })} className="mt-1.5" /></div>
                  <div><Label>Lng</Label><Input value={form.geo.lng} onChange={(e) => setForm({ ...form, geo: { ...form.geo, lng: e.target.value } })} className="mt-1.5" /></div>
                  <div><Label>{t("sites.geofence", "Geofence (m)")}</Label><Input type="number" value={form.geofenceRadius} onChange={(e) => setForm({ ...form, geofenceRadius: e.target.value })} className="mt-1.5" /></div>
                </div>
                <label className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
                  <span className="text-sm text-slate-700">{t("geo.enforce", "Enforce geofence — block clock in/out outside the radius")}</span>
                  <Switch data-testid="property-geofence-toggle" checked={!!form.geofenceEnabled} onCheckedChange={(v) => setForm({ ...form, geofenceEnabled: v })} />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <F label={t("prop.access", "Access")} k="accessInfo" icon={KeyRound} />
                  <F label={t("prop.parking", "Parking")} k="parkingInfo" icon={Car} />
                  <F label="what3words" k="what3words" icon={MapPin} />
                </div>
                <div><Label>{t("prop.instructions", "Instructions")}</Label><Textarea value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} className="mt-1.5" /></div>
                <div><Label>{t("common.notes", "Notes")}</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>{t("common.cancel", "Cancel")}</Button>
                <Button data-testid="property-save-btn" onClick={create} disabled={busy || !form.clientId || !form.address} className="bg-brand hover:bg-brand-dark">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.create", "Create")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-md flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="properties-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("prop.search", "Search by address, client, postcode, what3words…")} className="pl-9" />
        </div>
        <label className="flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer">
          <input type="checkbox" data-testid="properties-include-inactive" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
          {t("prop.includeInactive", "Include inactive")}
        </label>
        {can("property.manage") && <Button variant="outline" size="sm" data-testid="properties-manage-fields" onClick={() => setCfMgr(true)}><Settings2 className="h-3.5 w-3.5 mr-1.5" />{t("cf.manage", "Custom fields")}</Button>}
      </div>

      {can("property.manage") && selIds.length > 0 && (
        <BatchBar count={selIds.length} onClear={() => setSel({})} actions={[
          { key: "deactivate", label: "Deactivate", icon: <Power className="h-4 w-4 mr-1.5" />, className: "text-amber-600 border-amber-200", onClick: () => batch("deactivate") },
          { key: "reactivate", label: "Reactivate", icon: <Power className="h-4 w-4 mr-1.5" />, onClick: () => batch("reactivate") },
          { key: "delete", label: "Delete", icon: <Trash2 className="h-4 w-4 mr-1.5" />, className: "text-red-600 border-red-200", onClick: () => batch("delete") },
        ]} />
      )}

      <Card className="border-slate-200">
        <Table>
          <TableHeader><TableRow>
            {can("property.manage") && <TableHead className="w-8"><Checkbox data-testid="properties-select-all" checked={allSelected} onCheckedChange={toggleAll} /></TableHead>}
            <TableHead>{t("prop.address", "Address")}</TableHead>
            <TableHead>{t("prop.client", "Client")}</TableHead>
            <TableHead className="text-center">{t("sites.geofence", "Geofence")}</TableHead>
            <TableHead className="text-center">{t("prop.jobs", "Jobs")}</TableHead>
            <TableHead>{t("common.status", "Status")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10"><Home className="h-7 w-7 mx-auto mb-1" />{q ? t("common.noResults", "No matches") : "—"}</TableCell></TableRow> :
              filtered.map((p) => (
                <TableRow key={p.id} data-testid={`property-row-${p.id}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => nav(`/properties/${p.id}`)}>
                  {can("property.manage") && <TableCell className="py-2" onClick={(e) => e.stopPropagation()}><Checkbox data-testid={`property-select-${p.id}`} checked={!!sel[p.id]} onCheckedChange={(v) => setSel((s) => ({ ...s, [p.id]: !!v }))} /></TableCell>}
                  <TableCell className="font-medium text-slate-900"><span className="inline-flex items-center gap-1.5"><Home className="h-3.5 w-3.5 text-slate-400" />{p.address}</span></TableCell>
                  <TableCell className="text-slate-600"><span className="inline-flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5 text-brand" />{p.clientName}</span></TableCell>
                  <TableCell className="text-center"><span className="inline-flex items-center gap-1 text-xs text-slate-600"><Radar className={`h-3.5 w-3.5 ${p.geofenceEnabled ? "text-teal-500" : "text-slate-300"}`} />{p.geofenceRadius}m{p.geofenceEnabled ? "" : " · off"}</span></TableCell>
                  <TableCell className="text-center"><Badge variant="outline" className="bg-slate-50">{p.jobCount ?? 0}</Badge></TableCell>
                  <TableCell>{p.status === "deactivated" ? <Badge variant="outline" className="bg-slate-100 text-slate-500">{t("staff.inactive", "Inactive")}</Badge> : <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{t("st.active", "Active")}</Badge>}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>

      <CustomFieldManager entity="property" open={cfMgr} onOpenChange={setCfMgr} onChanged={reloadDefs} />
    </div>
  );
}
